/**
 * Zobrazenie workflow stavu CRM tiketu – parita ServiceTask (status_id + crm_states).
 */
import { displayCrmStateName, displayCrmStepName, crmSetupStateDisplayHex } from './crmStateI18n';

const COMMUNICATION_TYPES = new Set(['announcement', 'quote_request', 'acquisition', 'notice', 'inquiry']);

export function crmStateHexColor(raw) {
  return crmSetupStateDisplayHex(raw);
}

export function isCrmStateApplicableToTicketType(state, ticketType) {
  if (!state || !ticketType) return false;
  const aliases = state.applicable_types;
  if (!Array.isArray(aliases) || aliases.length === 0) return true;
  return aliases.some((a) => a === ticketType || (ticketType === 'quote_request' && a === 'inquiry') || (ticketType === 'announcement' && a === 'notice'));
}

/**
 * @param {object[]} crmStates
 * @param {string} [ticketType]
 */
export function workflowCrmStatesForTicketType(crmStates, ticketType) {
  const all = Array.isArray(crmStates) ? crmStates : [];
  if (!ticketType) {
    return all.filter((s) => {
      const types = s?.applicable_types;
      if (!Array.isArray(types) || types.length === 0) return true;
      return types.some((t) => COMMUNICATION_TYPES.has(t));
    });
  }
  return all.filter((s) => isCrmStateApplicableToTicketType(s, ticketType));
}

/**
 * Efektívne status_id pre filter (system_inspection + execution/non_final – parita FP).
 */
export function getEffectiveCrmTicketStatusId(rowData) {
  const statusId = Number(rowData?.status_id ?? rowData?.status_crm_state_id ?? 0);
  if (!statusId) return null;
  const crmStates = rowData?._crm_states_for_filter;
  if (!Array.isArray(crmStates)) return statusId;
  let status = crmStates.find((s) => Number(s.id) === statusId);
  if (!status) return statusId;
  if (status.phase === 'system_inspection') {
    const inspectionRows = Array.isArray(rowData?.inspection) ? rowData.inspection : [];
    for (const row of inspectionRows) {
      const inspectorStateId = Number(row?.inspector_state ?? 0);
      if (inspectorStateId > 0) {
        const inspectorState = crmStates.find((s) => Number(s.id) === inspectorStateId);
        if (inspectorState) {
          return Number(inspectorState.id);
        }
      }
    }
    const execStateId = rowData?.execution?.executor_state;
    if (execStateId) {
      const execState = crmStates.find((s) => Number(s.id) === Number(execStateId));
      if (execState && execState.phase === 'execution' && execState.transition_type === 'non_final') {
        return Number(execState.id);
      }
    }
  }
  return statusId;
}

/**
 * @param {object|null} ticket
 * @param {object[]} crmStates
 * @param {string} locale
 * @returns {{ id: number, name: string, color: string, phase?: string, transition_type?: string }|null}
 */
export function resolveCrmTicketDisplayStatus(ticket, crmStates, locale = 'sk') {
  if (!ticket) return null;
  const statusId = Number(ticket.status_id ?? ticket.status_crm_state_id ?? 0);
  if (!statusId) return null;
  const all = Array.isArray(crmStates) ? crmStates : [];
  let status = all.find((s) => Number(s.id) === statusId);
  if (!status) return null;

  if (status.phase === 'system_inspection') {
    const inspectionRows = Array.isArray(ticket.inspection) ? ticket.inspection : [];
    for (const row of inspectionRows) {
      const inspectorStateId = Number(row?.inspector_state ?? 0);
      if (inspectorStateId > 0) {
        const inspectorState = all.find((s) => Number(s.id) === inspectorStateId);
        if (inspectorState) {
          status = inspectorState;
          break;
        }
      }
    }
    if (status.phase === 'system_inspection') {
      const execStateId = ticket.execution?.executor_state;
      if (execStateId) {
        const execState = all.find((s) => Number(s.id) === Number(execStateId));
        if (execState && execState.phase === 'execution' && execState.transition_type === 'non_final') {
          status = execState;
        }
      }
    }
  }

  return {
    id: Number(status.id),
    name: displayCrmStateName(status.name, locale),
    color: crmStateHexColor(status.color),
    phase: status.phase,
    transition_type: status.transition_type,
  };
}

/**
 * @param {object[]} crmStates
 * @param {string} locale
 */
export function buildCrmWorkflowStatusFilterOptions(crmStates, locale = 'sk') {
  return workflowCrmStatesForTicketType(crmStates, null)
    .map((s) => ({
      label: displayCrmStateName(s.name, locale),
      value: Number(s.id),
    }))
    .filter((o) => o.label && !Number.isNaN(o.value))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

/** Terminálny workflow stav – parita FP AwaitingUserFormatter (skryté avatary, bez červeného deadlinu). */
export function isCrmTicketTerminalWorkflowStatus(rowData, crmStates) {
  if (rowData?.cancelled_at) return true;
  const statusId = Number(rowData?.status_id ?? rowData?.status_crm_state_id ?? 0);
  if (!statusId) return false;
  const states = Array.isArray(crmStates) ? crmStates : [];
  const state = states.find((s) => Number(s.id) === statusId);
  if (!state) return false;
  if (state.phase === 'closing' && state.transition_type !== 'reopen') return true;
  if (state.phase === 'approval' && state.transition_type === 'final_manually') return true;
  return false;
}

const EXECUTION_STEP_HIGHLIGHT_TRANSITIONS = new Set(['section_final', 'level_final', 'final_manually']);

/** Stav kroku „svieti“ pri ukončujúcej sekcii (Hotová, …). */
export function isExecutionStepStateHighlighted(rowData, crmStates = []) {
  const stateId = Number(rowData?.execution_step_state_id ?? 0);
  if (!stateId) return false;
  const transition = String(rowData?.execution_step_state_transition_type ?? '');
  if (EXECUTION_STEP_HIGHLIGHT_TRANSITIONS.has(transition)) return true;
  const state = (Array.isArray(crmStates) ? crmStates : []).find((s) => Number(s.id) === stateId);
  return state?.phase === 'execution'
    && EXECUTION_STEP_HIGHLIGHT_TRANSITIONS.has(String(state.transition_type ?? ''));
}

/**
 * Hlavný workflow stav pre stĺpec Stav (systémová úroveň, nie execution sub-stav).
 * @returns {{ id: number, name: string, color: string }|null}
 */
function resolveInspectorStateFromTicket(ticket, crmStates) {
  const inspectionRows = Array.isArray(ticket?.inspection) ? ticket.inspection : [];
  for (const row of inspectionRows) {
    const inspectorStateId = Number(row?.inspector_state ?? 0);
    if (inspectorStateId <= 0) continue;
    const inspectorState = (Array.isArray(crmStates) ? crmStates : []).find((s) => Number(s.id) === inspectorStateId);
    if (inspectorState) return inspectorState;
  }
  return null;
}

function applyInspectionDisplayOverrides(ticket, crmStates, status) {
  if (!status || !ticket) return status;
  const inspectorState = resolveInspectorStateFromTicket(ticket, crmStates);
  if (!inspectorState) return status;
  if (status.phase === 'system_inspection') {
    return inspectorState;
  }
  return status;
}

export function resolveCrmTicketListWorkflowStatus(ticket, crmStates, locale = 'sk') {
  if (!ticket) return null;
  const all = Array.isArray(crmStates) ? crmStates : [];
  const listId = Number(ticket.list_workflow_status_id ?? 0);
  const statusId = Number(ticket.status_id ?? ticket.status_crm_state_id ?? 0);
  const primaryId = listId > 0 ? listId : statusId;

  if (primaryId > 0) {
    const primaryState = all.find((s) => Number(s.id) === primaryId);
    const displayState = applyInspectionDisplayOverrides(ticket, all, primaryState) ?? primaryState;
    if (displayState) {
      // Prefer catalog JSON name for current UI locale. BE list_*_label is often SK-baked
      // while step labels may be EN — never lock display to the baked label when catalog exists.
      const effectiveId = Number(displayState.id);
      const colorRaw = (effectiveId === listId && ticket.list_workflow_status_color)
        ? ticket.list_workflow_status_color
        : displayState.color;
      return {
        id: effectiveId,
        name: displayCrmStateName(displayState.name, locale),
        color: crmStateHexColor(colorRaw),
      };
    }
  }

  const display = resolveCrmTicketDisplayStatus(ticket, all, locale);
  if (display) {
    return {
      id: display.id,
      name: display.name,
      color: display.color,
    };
  }

  const fallback = ticket.workflow_state_label ? String(ticket.workflow_state_label) : '';
  return fallback ? { id: statusId || 0, name: fallback, color: '#9ca3af' } : null;
}

/**
 * Druhý riadok stĺpca Stav – názov kroku alebo zvýraznený stav kroku.
 * @returns {{ mode: 'pill'|'text', label: string, color?: string }|null}
 */
function stepLabelFromExecutionPayload(step, locale = 'sk') {
  if (!step || typeof step !== 'object') return '';
  const fromName = displayCrmStepName(step.name, locale);
  if (fromName) return fromName;
  return String(step.label ?? step.title ?? '').trim();
}

/**
 * Doplní polia pre zobrazenie stavu zo zoznamu (list_workflow_status_*, execution_step_*)
 * z detail payloadu – parita ticketListRow / ticketDetailPayload.
 */
export function enrichCrmTicketForListStatusDisplay(ticket, crmStates, crmSteps, locale = 'sk') {
  if (!ticket || typeof ticket !== 'object') return ticket;
  const enriched = { ...ticket };
  const allStates = Array.isArray(crmStates) ? crmStates : [];
  const allSteps = Array.isArray(crmSteps) ? crmSteps : [];
  const statusId = Number(enriched.status_id ?? enriched.status_crm_state_id ?? 0);

  if (!Number(enriched.list_workflow_status_id ?? 0) && statusId > 0) {
    enriched.list_workflow_status_id = statusId;
    const state = allStates.find((s) => Number(s.id) === statusId);
    if (!String(enriched.list_workflow_status_label ?? '').trim() && state) {
      enriched.list_workflow_status_label = displayCrmStateName(state.name, locale);
    }
  }

  const exec = enriched.execution;
  if (exec && typeof exec === 'object') {
    if (!String(enriched.execution_crm_step_label ?? '').trim()) {
      const stepLabel = stepLabelFromExecutionPayload(exec.crm_step, locale)
        || stepLabelFromExecutionPayload(exec.step_definition, locale)
        || stepLabelFromExecutionPayload(exec.step_crm_state, locale);
      if (stepLabel) {
        enriched.execution_crm_step_label = stepLabel;
      } else {
        const stepId = Number(exec.crm_step_id ?? 0);
        const step = stepId > 0 ? allSteps.find((s) => Number(s.id) === stepId) : null;
        const catalogLabel = stepLabelFromExecutionPayload(step, locale);
        if (catalogLabel) {
          enriched.execution_crm_step_label = catalogLabel;
        }
      }
    }
    if (!Number(enriched.execution_step_state_id ?? 0) && exec.executor_state) {
      const executorStateId = Number(exec.executor_state);
      enriched.execution_step_state_id = executorStateId;
      const state = allStates.find((s) => Number(s.id) === executorStateId)
        || exec.executor_state_def;
      if (state) {
        if (!String(enriched.execution_step_state_label ?? '').trim()) {
          enriched.execution_step_state_label = displayCrmStateName(state.name, locale);
        }
        if (!String(enriched.execution_step_state_transition_type ?? '').trim()) {
          enriched.execution_step_state_transition_type = String(state.transition_type ?? '');
        }
      }
    }
  }

  const effectiveDisplay = resolveCrmTicketListWorkflowStatus(enriched, allStates, locale);
  if (effectiveDisplay?.id && effectiveDisplay?.name && Array.isArray(enriched.inspection) && enriched.inspection.length > 0) {
    const listId = Number(enriched.list_workflow_status_id ?? 0);
    const listState = listId > 0 ? allStates.find((s) => Number(s.id) === listId) : null;
    const shouldSync = Number(effectiveDisplay.id) !== listId
      || (listState?.phase === 'system_inspection' && effectiveDisplay.name !== String(enriched.list_workflow_status_label ?? '').trim());
    if (shouldSync) {
      enriched.list_workflow_status_id = Number(effectiveDisplay.id);
      enriched.list_workflow_status_label = effectiveDisplay.name;
    }
  }

  return enriched;
}

export function resolveCrmTicketStepLineDisplay(ticket, crmStates, locale = 'sk') {
  if (!ticket) return null;
  const all = Array.isArray(crmStates) ? crmStates : [];
  const statusId = Number(ticket.list_workflow_status_id ?? ticket.status_id ?? ticket.status_crm_state_id ?? 0);
  const mainStatus = statusId > 0 ? all.find((s) => Number(s.id) === statusId) : null;
  const mainIsExecutionPhase = mainStatus?.phase === 'execution';

  const rawStepLabel = String(ticket.execution_crm_step_label ?? '').trim();
  if (mainIsExecutionPhase && rawStepLabel) {
    return { mode: 'text', label: rawStepLabel };
  }

  // Pill only while main list status is execution (or unknown). System inspection waits
  // without duplicating the terminal step state on the second line.
  if (
    isExecutionStepStateHighlighted(ticket, crmStates)
    && (mainIsExecutionPhase || !mainStatus)
  ) {
    const stateId = Number(ticket.execution_step_state_id);
    if (stateId > 0 && stateId === statusId) {
      return null;
    }
    const state = all.find((s) => Number(s.id) === stateId);
    const label = (state ? displayCrmStateName(state.name, locale) : '')
      || String(ticket.execution_step_state_label ?? '').trim();
    if (!label) return null;
    return {
      mode: 'pill',
      label,
      color: crmStateHexColor(state?.color),
    };
  }

  const stepLabel = String(ticket.execution_step_label ?? '').trim();
  if (stepLabel && !mainIsExecutionPhase) {
    return { mode: 'text', label: stepLabel };
  }
  return null;
}
